import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Popover, Box } from "@mui/material"

import Icon from "@/components/icon/Icon"
import BasicButton from "@/components/button/BasicButton"
import MultiSelectFilter from "@/components/form/multiSelectFilter/MultiSelectFilter"
import Toggle from "@/components/form/toggle/Toggle"
import PlayerSelector from "@/app/report/components/filter/playerSelector/PlayerSelector"
import { createActivityCommonStore } from "@/app/activity/store"
import {
  ActivityType,
  ActivityTypeKey,
  MatchDayType,
  type ActivityParticipatedPlayerType,
} from "@/types/activity"
import { useSeasonWeekQuery, usePlayersQuery } from "@/hooks/api/useSettingQuery"
import { isEqualUnordered } from "@/app/report/common/tools"

import "@/app/report/components/filter/reportFilter.scss"
import "@/app/report/components/section/tacticMapFilter.scss"
import "./activityFilterPopover.scss"

interface Option<V extends string | number = string | number> {
  value: V
  label: string
}

// 시즌위크 미지정(None) 을 나타내는 가상 id — /filtered 의 idfSeasonWeek=-1 로 전송됨
const NONE_SEASON_WEEK_ID = -1

export default function ActivityFilterPopover() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const { filters } = createActivityCommonStore((state) => ({ filters: state.filters }))

  // PlayerSelector 가 portal 로 띄우는 dropdown 클릭 때문에 outer popover 가
  // backdropClick 으로 닫히는 케이스 방지 — 부모에서 open 상태 추적해 onClose reason 체크
  const [playerSelectorOpen, setPlayerSelectorOpen] = useState(false)

  const hasFilters =
    filters.types.length > 0 ||
    filters.matchDays.length > 0 ||
    filters.seasonWeekIds.length > 0 ||
    filters.playerIds.length > 0 ||
    filters.startOnMonday ||
    filters.alignToMatchDay

  const handleClose = (_e?: object, reason?: string) => {
    if (reason === "backdropClick" && playerSelectorOpen) {
      return
    }
    setAnchorEl(null)
  }

  return (
    <>
      <button
        className={`ActivityHeader__icon-btn ${hasFilters ? "ActivityHeader__icon-btn--active" : ""}`}
        aria-label="Filter"
        onClick={(e) => setAnchorEl(e.currentTarget)}>
        <Icon type="filter" />
        {hasFilters && <span className="ActivityHeader__icon-btn-dot" />}
      </button>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 1, zIndex: 8 }}
        disableScrollLock>
        {/* 팝오버가 열릴 때만 마운트 → season/players 쿼리도 그때만 실행되고 draft 도 fresh init */}
        {anchorEl && (
          <FilterContent
            onClose={() => setAnchorEl(null)}
            playerSelectorOpen={playerSelectorOpen}
            setPlayerSelectorOpen={setPlayerSelectorOpen}
          />
        )}
      </Popover>
    </>
  )
}

const TYPE_KEYS = Object.values(ActivityTypeKey) as ActivityTypeKey[]
const MATCH_DAY_KEYS = Object.values(MatchDayType) as MatchDayType[]

function FilterContent({
  onClose,
  playerSelectorOpen,
  setPlayerSelectorOpen,
}: {
  onClose: () => void
  playerSelectorOpen: boolean
  setPlayerSelectorOpen: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { filters, setFilters, viewMode } = createActivityCommonStore((state) => ({
    filters: state.filters,
    setFilters: state.setFilters,
    viewMode: state.viewMode,
  }))

  const showStartOnMonday = viewMode === "month" || viewMode === "week"
  const showAlignToMatchDay = viewMode === "week"

  const { data: seasonWeekData } = useSeasonWeekQuery()
  const { data: playersData } = usePlayersQuery()

  // ──── draft state — Apply 누를 때만 store 에 commit ────
  // store.filters 의 빈 배열 = "전체" 의미. 화면에서는 모든 옵션이 체크된 상태로 표현
  // (= ALL_TYPES 등으로 prefill). 사용자가 일부 해제하면 그 부분집합이 draft 가 됨.
  const ALL_TYPES = TYPE_KEYS
  const ALL_MATCH_DAYS = MATCH_DAY_KEYS
  const [draftTypes, setDraftTypes] = useState<ActivityTypeKey[]>(
    filters.types.length ? filters.types : ALL_TYPES,
  )
  const [draftSeasons, setDraftSeasons] = useState<number[]>(filters.seasonWeekIds)
  const [draftMatchDays, setDraftMatchDays] = useState<MatchDayType[]>(
    filters.matchDays.length ? filters.matchDays : ALL_MATCH_DAYS,
  )
  const [draftPlayers, setDraftPlayers] = useState<ActivityParticipatedPlayerType[]>([])
  const [draftStartOnMonday, setDraftStartOnMonday] = useState(filters.startOnMonday)
  const [draftAlignToMatchDay, setDraftAlignToMatchDay] = useState(filters.alignToMatchDay)

  // PlayerSelector 가 ActivityParticipatedPlayerType 만 받음 → PlayerSetting 어댑터
  const allPlayers = useMemo<ActivityParticipatedPlayerType[]>(() => {
    return (playersData ?? []).map((p) => ({
      idfPlayer: p.idfPlayer,
      nickname: p.nickname,
      name: p.name,
      position: p.position,
      image: p.image,
      uniformNumber: p.uniformNumber,
      backNumber: p.uniformNumber,
      idfPlayerGroup: p.idfPlayerGroup ?? 0,
      playTime: 0,
    }))
  }, [playersData])

  // playersData 도착 시 draftPlayers seed — store 가 비어있으면 전체 선택, 아니면 저장된 ids
  const seededPlayersRef = useRef(false)
  useEffect(() => {
    if (seededPlayersRef.current || !allPlayers.length) {
      return
    }
    setDraftPlayers(
      filters.playerIds.length
        ? allPlayers.filter((p) => filters.playerIds.includes(p.idfPlayer))
        : allPlayers,
    )
    seededPlayersRef.current = true
  }, [allPlayers, filters.playerIds])

  // seasonOptions 도착 시 draftSeasons 초기화 — store 가 비면 전체 선택
  const seededSeasonsRef = useRef(false)

  // ──── options ────
  const typeOptions = useMemo<Option<ActivityTypeKey>[]>(
    () =>
      TYPE_KEYS.map((k) => ({
        value: k,
        label: t(ActivityType[k as keyof typeof ActivityType] ?? String(k)),
      })),
    [t],
  )

  const matchDayOptions = useMemo<Option<MatchDayType>[]>(
    () => MATCH_DAY_KEYS.map((m) => ({ value: m, label: String(m) })),
    [],
  )

  const seasonOptions = useMemo<Option<number>[]>(() => {
    return (seasonWeekData?.seasonWeeks ?? [])
      .filter(
        (sw): sw is typeof sw & { idfSeasonWeek: number } =>
          sw.idfSeasonWeek !== null && sw.idfSeasonWeek !== undefined,
      )
      .map((sw) => ({ value: sw.idfSeasonWeek, label: sw.name ?? `#${sw.idfSeasonWeek}` }))
  }, [seasonWeekData])

  useEffect(() => {
    if (seededSeasonsRef.current || !seasonOptions.length) {
      return
    }
    setDraftSeasons(
      filters.seasonWeekIds.length
        ? filters.seasonWeekIds
        : [...seasonOptions.map((o) => o.value), NONE_SEASON_WEEK_ID],
    )
    seededSeasonsRef.current = true
  }, [seasonOptions, filters.seasonWeekIds])

  // Reset = 모든 옵션 다시 체크 (default = All), 토글은 off
  const handleReset = () => {
    setDraftTypes(ALL_TYPES)
    setDraftSeasons([...seasonOptions.map((o) => o.value), NONE_SEASON_WEEK_ID])
    setDraftMatchDays(ALL_MATCH_DAYS)
    setDraftPlayers(allPlayers)
    setDraftStartOnMonday(false)
    setDraftAlignToMatchDay(false)
  }

  // 전체 선택 케이스는 store 에 [] 로 collapse — 필터 활성/비활성 표시와 일관
  const draftCommitted = useMemo(() => {
    return {
      types: draftTypes.length === ALL_TYPES.length ? [] : draftTypes,
      seasonWeekIds:
        seasonOptions.length > 0 && draftSeasons.length === seasonOptions.length + 1
          ? []
          : draftSeasons,
      matchDays: draftMatchDays.length === ALL_MATCH_DAYS.length ? [] : draftMatchDays,
      playerIds:
        allPlayers.length > 0 && draftPlayers.length === allPlayers.length
          ? []
          : draftPlayers.map((p) => p.idfPlayer),
      startOnMonday: draftStartOnMonday,
      alignToMatchDay: draftAlignToMatchDay,
    }
  }, [
    draftTypes,
    draftSeasons,
    draftMatchDays,
    draftPlayers,
    seasonOptions,
    allPlayers,
    ALL_TYPES,
    ALL_MATCH_DAYS,
    draftStartOnMonday,
    draftAlignToMatchDay,
  ])

  // TacticMapFilter 와 동일하게 — draft 가 현재 store 와 같으면 Apply 비활성
  const applyDisabled = useMemo(() => {
    return (
      isEqualUnordered(draftCommitted.types, filters.types) &&
      isEqualUnordered(draftCommitted.seasonWeekIds, filters.seasonWeekIds) &&
      isEqualUnordered(draftCommitted.matchDays, filters.matchDays) &&
      isEqualUnordered(draftCommitted.playerIds, filters.playerIds) &&
      draftCommitted.startOnMonday === filters.startOnMonday &&
      draftCommitted.alignToMatchDay === filters.alignToMatchDay
    )
  }, [draftCommitted, filters])

  const handleApply = () => {
    setFilters(draftCommitted)
    onClose()
  }

  return (
    <div className="tactic-map-filter-popover ActivityFilterPopover">
      {/* Activity Type */}
      <Box className="tactic-map-filter-menu">
        <Box className="tactic-map-filter-menu-box report-filter-selector">
          <MultiSelectFilter
            label={t("activityType")}
            options={typeOptions}
            selected={draftTypes}
            onChange={setDraftTypes}
          />
        </Box>
      </Box>

      {/* Season */}
      <Box className="tactic-map-filter-menu">
        <Box className="tactic-map-filter-menu-box report-filter-selector">
          <MultiSelectFilter
            label={t("season")}
            options={seasonOptions}
            selected={draftSeasons}
            onChange={setDraftSeasons}
            noneOption={{ value: NONE_SEASON_WEEK_ID, label: t("none") }}
          />
        </Box>
      </Box>

      {/* Match Day */}
      <Box className="tactic-map-filter-menu">
        <Box className="tactic-map-filter-menu-box report-filter-selector">
          <MultiSelectFilter
            label={t("matchDay")}
            options={matchDayOptions}
            selected={draftMatchDays}
            onChange={setDraftMatchDays}
          />
        </Box>
      </Box>

      {/* Player — TacticMap 과 동일하게 PlayerSelector 임베드 */}
      <Box className="tactic-map-filter-menu">
        <Box className="tactic-map-filter-menu-box tactic-map-player-filter">
          <PlayerSelector
            open={playerSelectorOpen}
            setOpen={setPlayerSelectorOpen}
            selectedActivity={[]}
            players={allPlayers.length > 0 ? allPlayers : undefined}
            selectedPlayerList={draftPlayers}
            setSelectedPlayerList={setDraftPlayers}
            paramsVersion={false}
          />
        </Box>
      </Box>

      {(showStartOnMonday || showAlignToMatchDay) && (
        <div className="ActivityFilterPopover__toggleSection">
          {showStartOnMonday && (
            <label className="ActivityFilterPopover__toggleRow">
              <Toggle
                checked={draftStartOnMonday}
                onChange={(_, checked) => setDraftStartOnMonday(checked)}
              />
              <span className="ActivityFilterPopover__toggleLabel">
                {t("startOnMonday", { defaultValue: "Start on Monday" })}
              </span>
            </label>
          )}
          {showAlignToMatchDay && (
            <label className="ActivityFilterPopover__toggleRow">
              <Toggle
                checked={draftAlignToMatchDay}
                onChange={(_, checked) => setDraftAlignToMatchDay(checked)}
              />
              <span className="ActivityFilterPopover__toggleLabel">
                {t("alignToMatchDay", { defaultValue: "Align to Match Day" })}
              </span>
            </label>
          )}
        </div>
      )}

      <Box className="tactic-map-filter-reset-apply-button">
        <Box className="tactic-map-filter-reset-apply-button-box">
          <div className="tactic-map-reset-button">
            <BasicButton
              type="primary-contained"
              size="sm"
              disabled={applyDisabled}
              onClick={handleReset}>
              <Icon type="reset" size={20} />
              <div className="tactic-map-reset-button-text">{t("button.reset")}</div>
            </BasicButton>
          </div>
          <div className="tactic-map-apply-button">
            <BasicButton type="primary-contained" disabled={applyDisabled} onClick={handleApply}>
              <div className="tactic-map-apply-button-text">{t("button.apply")}</div>
            </BasicButton>
          </div>
        </Box>
      </Box>
    </div>
  )
}
